import React, { useState, useEffect } from "react";
import {
  Modal,
  Box,
  Typography,
  IconButton,
  Checkbox,
  FormControlLabel,
  Grid,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableContainer,
  Paper,
  Avatar,
  Dialog,
  DialogContent,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  doc,
  updateDoc,
  collection,
  query,
  where,
  getDocs,
  onSnapshot,
} from "firebase/firestore";
import { db } from "../../firebase";
import TextField from "@mui/material/TextField";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";

function GroupAttendanceModal({ open, onClose, formData }) {
  const [selectedShift, setSelectedShift] = useState(0);
  const [enlargedImage, setEnlargedImage] = useState(null);
  const [openImageDialog, setOpenImageDialog] = useState(false);
  // Les clés de editModes seront : "clientId_depart" et "clientId_arrived"
  const [editModes, setEditModes] = useState({});
  // Les clés de localNotes seront : "clientId_depart" et "clientId_arrived"
  const [localNotes, setLocalNotes] = useState({});

  // Récupérer la note de départ depuis localAttendances pour un client
  const getClientNote = (clientId) => {
    return (
      (localAttendances &&
        localAttendances[selectedShift] &&
        localAttendances[selectedShift][clientId] &&
        localAttendances[selectedShift][clientId].note) ||
      ""
    );
  };

  // Récupérer la note d'arrivée depuis localAttendances pour un client
  const getClientNoteArrived = (clientId) => {
    return (
      (localAttendances &&
        localAttendances[selectedShift] &&
        localAttendances[selectedShift][clientId] &&
        localAttendances[selectedShift][clientId].noteArrived) ||
      ""
    );
  };

  // Mise à jour du state localNotes lors d'une modification dans le TextField
  const handleNoteChange = (key, event) => {
    const newText = event.target.value;
    setLocalNotes((prev) => ({
      ...prev,
      [key]: newText,
    }));
  };

  // Bascule le mode édition et sauvegarde la note en fin d'édition
  const handleToggleEdit = (clientId, isArrived = false) => {
    const key = clientId + (isArrived ? "_arrived" : "_depart");
    const isEditing = editModes[key] || false;
    if (isEditing) {
      // Création d'une copie des attendances
      const updatedAttendances = localAttendances
        ? [...localAttendances]
        : Array.from({ length: 11 }, () => ({}));
      const currentShiftData = updatedAttendances[selectedShift] || {};
      const clientInfo = inhouseClients.find((c) => c.id === clientId) || {};
      const existingAttendance = currentShiftData[clientId] || {
        id: clientId,
        firstName: clientInfo.firstName || "",
        lastName: clientInfo.lastName || "",
        profilePicture: clientInfo.profilePicture || "",
      };
      // Récupération de la nouvelle note (pour depart ou arrived)
      const newNote =
        localNotes[key] !== undefined
          ? localNotes[key]
          : isArrived
          ? getClientNoteArrived(clientId)
          : getClientNote(clientId);
      currentShiftData[clientId] = {
        ...existingAttendance,
        ...(isArrived ? { noteArrived: newNote } : { note: newNote }),
      };
      updatedAttendances[selectedShift] = currentShiftData;
      setLocalAttendances(updatedAttendances);
      const docRef = doc(db, "groupAttendanceForms", formData.id);
      updateDoc(docRef, { attendances: updatedAttendances }).catch((error) =>
        console.error("Erreur lors de la sauvegarde de la note:", error)
      );
    }
    setEditModes((prev) => ({ ...prev, [key]: !isEditing }));
  };

  const SHIFT_DEFINITIONS = [
    { time: "7:30 AM", name: "BKF" },
    { time: "8:30 AM", name: "CHECK-IN" },
    { time: "9:00 AM", name: "P. GROUP" },
    { time: "10:30 AM", name: "CBT/ 8 Step" },
    { time: "11:40 AM", name: "CBT / Holistic" },
    { time: "12:30 PM", name: "Lunch" },
    { time: "1:15 PM", name: "Group" },
    { time: "3:00 PM", name: "Community" },
    { time: "4:00 PM", name: "Afternoon Sessions" },
    { time: "5:00 PM", name: "SWOT/ 12Step" },
    { time: "6:00 PM", name: "Dinner" },
    { time: "7:00 PM", name: "Meeting" },
  ];

  const handleAvatarClick = (imageUrl) => {
    const url =
      imageUrl || "https://via.placeholder.com/150/CCCCCC/FFFFFF/?text=Avatar";
    setEnlargedImage(url);
    setOpenImageDialog(true);
  };

  const handleCloseImageDialog = () => {
    setOpenImageDialog(false);
    setEnlargedImage(null);
  };

  // State local pour stocker les attendances (afin que le rendu réagisse immédiatement)
  const [localAttendances, setLocalAttendances] = useState(
    formData.attendances || Array.from({ length: 11 }, () => ({}))
  );
  const [inhouseClients, setInhouseClients] = useState([]);

  // Synchronisation de localAttendances quand formData change (par exemple à l'ouverture du modal)
  useEffect(() => {
    setLocalAttendances(
      formData.attendances || Array.from({ length: 11 }, () => ({}))
    );
  }, [formData]);

  // Récupération des clients "In House"
  useEffect(() => {
    async function fetchInhouseClients() {
      const clientsRef = collection(db, "clients");
      const clientQuery = query(
        clientsRef,
        where("clientStatus", "==", "In House")
      );
      const snapshot = await getDocs(clientQuery);
      const clientsData = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setInhouseClients(clientsData);
    }
    fetchInhouseClients();
  }, []);

  const handleShiftChange = (event) => {
    setSelectedShift(parseInt(event.target.value, 10));
  };

  const handleCheckboxChange = async (clientId, type, event) => {
    const newValue = event.target.checked;
    // Création d'une copie du state local
    const updatedAttendances = localAttendances
      ? [...localAttendances]
      : Array.from({ length: 11 }, () => ({}));
    const currentShiftData = updatedAttendances[selectedShift] || {};
    const clientInfo = inhouseClients.find((c) => c.id === clientId) || {};
    const existingAttendance = currentShiftData[clientId] || {
      id: clientId,
      firstName: clientInfo.firstName || "",
      lastName: clientInfo.lastName || "",
      profilePicture: clientInfo.profilePicture || "",
    };
    currentShiftData[clientId] = {
      ...existingAttendance,
      [type]: newValue,
    };
    updatedAttendances[selectedShift] = currentShiftData;
    setLocalAttendances(updatedAttendances);
    try {
      const docRef = doc(db, "groupAttendanceForms", formData.id);
      await updateDoc(docRef, { attendances: updatedAttendances });
    } catch (error) {
      console.error("Erreur lors de la mise à jour de l'attendance: ", error);
    }
  };

  const getCheckboxValue = (clientId, type) => {
    if (!localAttendances || !localAttendances[selectedShift]) return false;
    const clientAttendance = localAttendances[selectedShift][clientId];
    return clientAttendance ? clientAttendance[type] || false : false;
  };

  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 600,
    bgcolor: "background.paper",
    boxShadow: 24,
    p: 4,
    maxHeight: "90vh",
    overflowY: "auto",
  };

  useEffect(() => {
    const docRef = doc(db, "groupAttendanceForms", formData.id);
    const unsubscribe = onSnapshot(docRef, (docSnap) => {
      if (docSnap.exists()) {
        const attendancesFromFirestore = docSnap.data().attendances;
        // Comparer pour éviter des mises à jour redondantes
        if (
          JSON.stringify(attendancesFromFirestore) !==
          JSON.stringify(localAttendances)
        ) {
          setLocalAttendances(attendancesFromFirestore);
        }
      }
    });
    return () => unsubscribe();
  }, [formData.id]);

  return (
    <Modal open={open} onClose={onClose}>
      <>
        <Box sx={style}>
          <Grid container justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              Clients G.A.R for Staff - {formData.date}
            </Typography>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Grid>
          {/* Sélecteur de shift */}
          <Box mt={2}>
            <FormControl fullWidth>
              <InputLabel id="shift-select-label">Select Shift</InputLabel>
              <Select
                labelId="shift-select-label"
                value={selectedShift}
                label="Select Shift"
                onChange={handleShiftChange}
              >
                {Array.from({ length: 11 }).map((_, index) => (
                  <MenuItem key={index} value={index}>
                    {`Shift ${index + 1} - ${SHIFT_DEFINITIONS[index].time} - ${
                      SHIFT_DEFINITIONS[index].name
                    }`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          {/* Liste des clients inhouse */}
          <Box mt={2}>
            <Typography variant="subtitle1">Inhouse Clients</Typography>
            <TableContainer component={Paper}>
              <Table size="small">
                <TableBody>
                  {inhouseClients.map((client) => (
                    <TableRow
                      key={client.id}
                      style={{
                        backgroundColor:
                          getCheckboxValue(client.id, "incoming") &&
                          getCheckboxValue(client.id, "arrived")
                            ? "lightgreen"
                            : "inherit",
                        padding: "8px 0",
                        border: "1px solid lightgrey",
                      }}
                    >
                      {/* Avatar et Nom */}
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          height: "110px",
                          flexDirection: "column",
                          paddingTop: "5px",
                        }}
                      >
                        <Avatar
                          src={
                            client.profilePicture ||
                            "https://via.placeholder.com/40/CCCCCC/FFFFFF/?text=Avatar"
                          }
                          alt={`${client.firstName} ${client.lastName}`}
                          sx={{ width: 70, height: 70, cursor: "pointer" }}
                          onClick={() =>
                            handleAvatarClick(client.profilePicture)
                          }
                        />
                        <Typography
                          variant="body1"
                          style={{
                            fontWeight: 500,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            maxWidth: "150px",
                          }}
                        >
                          {client.firstName} {client.lastName}
                        </Typography>
                      </div>

                      {/* Colonne pour la note de départ */}
                      <TableCell>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "4px",
                            height: "100px",
                          }}
                        >
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={getCheckboxValue(
                                  client.id,
                                  "incoming"
                                )}
                                onChange={(e) =>
                                  handleCheckboxChange(client.id, "incoming", e)
                                }
                              />
                            }
                            label="Incoming"
                          />
                          {editModes[client.id + "_depart"] ? (
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "4px",
                              }}
                            >
                              <TextField
                                multiline
                                rows={2}
                                value={
                                  localNotes[client.id + "_depart"] !==
                                  undefined
                                    ? localNotes[client.id + "_depart"]
                                    : getClientNote(client.id)
                                }
                                onChange={(e) =>
                                  handleNoteChange(client.id + "_depart", e)
                                }
                                variant="outlined"
                                size="small"
                                style={{
                                  flex: 1,
                                  maxWidth: "200px",
                                  height: "48px",
                                  overflow: "auto",
                                }}
                                InputProps={{
                                  style: {
                                    padding: "4px 8px",
                                    fontSize: "0.875rem",
                                    lineHeight: 1.5,
                                  },
                                }}
                              />
                              <IconButton
                                onClick={() =>
                                  handleToggleEdit(client.id, false)
                                }
                                size="small"
                              >
                                <SaveIcon fontSize="small" />
                              </IconButton>
                            </div>
                          ) : (
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "4px",
                              }}
                            >
                              <TextField
                                value={getClientNote(client.id) || "No note"}
                                variant="outlined"
                                size="small"
                                disabled
                                multiline
                                InputProps={{
                                  style: {
                                    padding: "4px 8px",
                                    fontSize: "0.875rem",
                                    lineHeight: 1.5,
                                    overflow: "auto",
                                  },
                                }}
                                style={{
                                  flex: 1,
                                  maxWidth: "200px",
                                  height: "48px",
                                }}
                              />
                              <IconButton
                                onClick={() =>
                                  handleToggleEdit(client.id, false)
                                }
                                size="small"
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </div>
                          )}
                        </div>
                      </TableCell>

                      {/* Colonne pour la note d'arrivée */}
                      <TableCell>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "4px",
                            height: "100px",
                          }}
                        >
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={getCheckboxValue(client.id, "arrived")}
                                onChange={(e) =>
                                  handleCheckboxChange(client.id, "arrived", e)
                                }
                              />
                            }
                            label="Arrived"
                          />
                          {editModes[client.id + "_arrived"] ? (
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "4px",
                              }}
                            >
                              <TextField
                                multiline
                                rows={2}
                                value={
                                  localNotes[client.id + "_arrived"] !==
                                  undefined
                                    ? localNotes[client.id + "_arrived"]
                                    : getClientNoteArrived(client.id)
                                }
                                onChange={(e) =>
                                  handleNoteChange(client.id + "_arrived", e)
                                }
                                variant="outlined"
                                size="small"
                                style={{
                                  flex: 1,
                                  maxWidth: "200px",
                                  height: "48px",
                                  overflow: "auto",
                                }}
                                InputProps={{
                                  style: {
                                    padding: "4px 8px",
                                    fontSize: "0.875rem",
                                    lineHeight: 1.5,
                                  },
                                }}
                              />
                              <IconButton
                                onClick={() =>
                                  handleToggleEdit(client.id, true)
                                }
                                size="small"
                              >
                                <SaveIcon fontSize="small" />
                              </IconButton>
                            </div>
                          ) : (
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "4px",
                              }}
                            >
                              <TextField
                                value={
                                  getClientNoteArrived(client.id) || "No note"
                                }
                                variant="outlined"
                                size="small"
                                disabled
                                multiline
                                InputProps={{
                                  style: {
                                    padding: "4px 8px",
                                    fontSize: "0.875rem",
                                    lineHeight: 1.5,
                                    overflow: "auto",
                                  },
                                }}
                                style={{
                                  flex: 1,
                                  maxWidth: "200px",
                                  height: "48px",
                                }}
                              />
                              <IconButton
                                onClick={() =>
                                  handleToggleEdit(client.id, true)
                                }
                                size="small"
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </div>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>
        <Dialog open={openImageDialog} onClose={handleCloseImageDialog}>
          <DialogContent>
            {enlargedImage && (
              <img
                src={enlargedImage}
                alt="Profile agrandi"
                style={{
                  width: "100%",
                  maxWidth: "500px",
                  display: "block",
                  margin: "auto",
                }}
              />
            )}
          </DialogContent>
        </Dialog>
      </>
    </Modal>
  );
}

export default GroupAttendanceModal;
